import * as React from 'react'
import type { GatsbyBrowser } from 'gatsby'

import '@/styles/index.css'
import CookieBanner from '@/components/CookieBanner'
import DeferredMount from '@/components/DeferredMount'
import Footer from '@/components/Footer'
import Navbar from '@/components/Navbar'

/**
 * Wraps every page with a persistent Navbar so it doesn't remount on navigation.
 * The Footer is wrapped in DeferredMount (mounts on first interaction) to match
 * gatsby-ssr and keep it off the initial hydration / LCP critical path.
 * @param props - Gatsby page element and props
 * @returns Page wrapped with Navbar
 */
export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({ element, props }) => {
	return (
		<>
			<Navbar />
			{ element }
			<DeferredMount>
				<Footer hideAnimation={ ['/404', '/contact'].some(p => props.pageResources?.page?.path?.includes(p)) } />
			</DeferredMount>
			<CookieBanner />
		</>
	)
}

/**
 * Remove the fonts-loading class once all fonts are ready (or after 1.5 s as a safety fallback).
 * This prevents FOUT by keeping the page invisible until the custom fonts are available.
 */
export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
	const reveal = () => document.documentElement.classList.remove('fonts-loading')
	const timeout = new Promise<void>(resolve => setTimeout(resolve, 1500))
	Promise.race([document.fonts.ready, timeout]).then(reveal)
}

/**
 * Kill all active ScrollTrigger instances before each route change.
 * Uses a dynamic import so GSAP stays out of the initial bundle — it will
 * already be in the browser cache by the time navigation happens.
 */
export const onPreRouteUpdate: GatsbyBrowser['onPreRouteUpdate'] = () => {
	import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
		ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
	}).catch(() => {})
}

/**
 * After route change, scroll to top instantly and refresh ScrollTrigger.
 * `behavior: 'instant'` bypasses the `scroll-smooth` class on <html> so the
 * page lands at the top immediately rather than animating from the previous
 * scroll position — important because pin-ScrollTriggers refresh right after
 * and an in-flight smooth scroll would fight the new pins.
 */
export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
	if (location.hash) {
		// getElementById (with decoded hash) is used instead of querySelector so
		// non-ASCII ids (e.g. Thai) work — location.hash is URL-encoded and `%`
		// is invalid in CSS selectors.
		// Many sections are React.lazy + Suspense, so the target — and the lazy
		// components above it — keep mounting after route change, which both
		// delays the target appearing and shifts its position. We wait for the
		// element to exist, then debounce until DOM mutations settle, then run a
		// single smooth scroll so the animation has a stable destination.
		const id = decodeURIComponent(location.hash.slice(1))
		let settleTimer: number | undefined
		let bailTimer: number | undefined

		const smoothScroll = () => {
			document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
		}

		const observer = new MutationObserver(() => {
			if (!document.getElementById(id)) return
			if (settleTimer !== undefined) window.clearTimeout(settleTimer)
			settleTimer = window.setTimeout(() => {
				smoothScroll()
				observer.disconnect()
				if (bailTimer !== undefined) window.clearTimeout(bailTimer)
			}, 200)
		})
		observer.observe(document.body, { childList: true, subtree: true })
		bailTimer = window.setTimeout(() => observer.disconnect(), 5000)

		// In case the target already exists and no further mutations occur,
		// schedule a scroll attempt on the next frame.
		requestAnimationFrame(() => {
			if (document.getElementById(id) && settleTimer === undefined) {
				settleTimer = window.setTimeout(() => {
					smoothScroll()
					observer.disconnect()
					if (bailTimer !== undefined) window.clearTimeout(bailTimer)
				}, 200)
			}
		})
	} else {
		window.scrollTo({ top: 0, behavior: 'instant' })
	}
	import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
		ScrollTrigger.refresh()
	}).catch(() => {})
}

/**
 * Override Gatsby's scroll restoration so every navigation always lands at the
 * top of the page. Returning false tells Gatsby not to manage the scroll position;
 * onRouteUpdate handles it instead.
 */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({ routerProps: { location } }) => {
	if (location.hash) return false
	window.scrollTo({ top: 0, behavior: 'instant' })
	return false
}
